import clsx from 'clsx';
import { RichTextContent } from '../richtText';
import type { KeyVisualProps, KeyVisualClassicProps, KeyVisualEditorialProps, KeyVisualNonClassicProps } from './types';
import { AwardCollection } from '../awardCollection';
import './styles.css';
import { getAwardOrientation, getFeaturedAward, hasShoppableHotspots, shouldRenderAwardCollection } from './utils';
import {
  KeyVisualClassic,
  KeyVisualNonClassic,
  KeyVisualEditorial,
  KeyVisualMagazine,
  KeyVisualDownload,
  KeyVisualNewsletterMagazine
} from './variants';
import { KEYVISUAL_TYPE } from '../../constants';
import { isRichTextEmpty } from '../richtText/utils/isRichTextEmpty';
import type { NewsletterMagazineNode } from 'data-layer';

function isNewsletterMagazineKeyVisual(keyVisual: KeyVisualProps['keyVisual']): keyVisual is NewsletterMagazineNode {
  return keyVisual.transformerType === 'NewsletterMagazineTransformer';
}

/**
 * KeyVisual component - Hero section for landing pages.
 *
 * Displays a prominent visual section with image, text content, action buttons,
 * ratings, awards, and optional vouchers. Supports different visual types including
 * classic, high-contrast, highlight, and blog/editorial variations.
 *
 * @param props - KeyVisual component properties
 * @returns Rendered key visual section
 *
 * @example
 * // Basic key visual with image and actions
 * <KeyVisual
 *   device="desktop"
 *   keyVisual={{
 *     id: 'hero-1',
 *     keyVisualType: 'Classic',
 *     title: 'Create Beautiful Photo Books',
 *     subtitle: 'Premium quality printing',
 *     image: { url: '/hero.jpg', alt: 'Photo book' },
 *     links: [
 *       { text: 'Shop Now', url: '/shop' },
 *       { text: 'Learn More', url: '/about' },
 *     ],
 *     // ... other properties
 *   }}
 *   awards={[]}
 * />
 *
 * @example
 * // Key visual with awards and rating
 * <KeyVisual
 *   device="mobile"
 *   keyVisual={{
 *     id: 'hero-2',
 *     keyVisualType: 'High-Contrast with Awards',
 *     // ... other properties
 *   }}
 *   awards={[
 *     { image: { url: '/award1.png' } },
 *     { image: { url: '/award2.png' } },
 *   ]}
 *   customerReviews={{
 *     rating: 4.8,
 *     ratingDescription: '1,250 reviews',
 *   }}
 * />
 *
 * @example
 * // Key visual with voucher
 * <KeyVisual
 *   device="desktop"
 *   keyVisual={{...}}
 *   awards={[]}
 *   voucher={{
 *     code: 'WELCOME20',
 *     value: 20,
 *     type: 'percentage',
 *     headline: 'Special Offer',
 *     // ... other voucher properties
 *   }}
 * />
 *
 * @example
 * // Blog/Editorial key visual (no default background)
 * <KeyVisual
 *   device="desktop"
 *   keyVisual={{
 *     id: 'blog-hero',
 *     keyVisualType: 'Blog',
 *     title: 'Photography Tips & Inspiration',
 *     richText: { ... },
 *     image: { url: '/blog-hero.jpg', alt: 'Blog header' },
 *     // ... other properties
 *   }}
 *   awards={[]}
 * />
 */
export function KeyVisual(props: KeyVisualProps) {
  const { keyVisual, awards, className } = props;
  const isNewsletterMagazineType = isNewsletterMagazineKeyVisual(keyVisual);
  const transformerType = keyVisual.transformerType;
  const theme = isNewsletterMagazineType ? keyVisual.magazine.theme : keyVisual.theme;
  const image = isNewsletterMagazineType ? keyVisual.magazine.image : keyVisual.image;
  const cmsId = isNewsletterMagazineType ? keyVisual.magazine.id : keyVisual.id;

  // Type-safe access to properties based on node type
  const keyVisualType = 'keyVisualType' in keyVisual ? keyVisual.keyVisualType : undefined;
  const richText = 'richText' in keyVisual ? keyVisual.richText : undefined;

  // Compute derived values
  const themeCss = theme?.cssClassName;
  const text = richText && !isRichTextEmpty(richText) && <RichTextContent node={richText} />;
  const hasHotspots = hasShoppableHotspots(image);
  const showAwardCollection = shouldRenderAwardCollection(awards, keyVisualType);
  const featuredAward = getFeaturedAward(awards, showAwardCollection);
  const awardOrientation = getAwardOrientation(featuredAward);
  const isClassicType = keyVisualType === '' || keyVisualType === KEYVISUAL_TYPE.CLASSIC;
  const isBlogType = keyVisualType === KEYVISUAL_TYPE.BLOG;
  const isMagazineType = transformerType === 'MagazineTransformer';
  const isDownloadType = keyVisualType === KEYVISUAL_TYPE.DOWNLOAD;

  // Prepare variant props
  const variantProps = {
    ...props,
    hasHotspots,
    featuredAward,
    awardOrientation,
    text
  };

  return (
    <section
      id={cmsId}
      className={clsx(
        'main-section',
        'key-visual__wrapper',
        themeCss,
        isBlogType && 'center--content',
        isDownloadType && 'key-visual__download',
        className
      )}
      role="region"
      aria-labelledby="key-visual-heading"
    >
      {isClassicType ? (
        <KeyVisualClassic {...(variantProps as KeyVisualClassicProps)} />
      ) : isDownloadType ? (
        <KeyVisualDownload {...(variantProps as KeyVisualClassicProps)} />
      ) : isBlogType ? (
        <KeyVisualEditorial {...(variantProps as KeyVisualEditorialProps)} />
      ) : isNewsletterMagazineType ? (
        <KeyVisualNewsletterMagazine {...variantProps} />
      ) : isMagazineType ? (
        <KeyVisualMagazine {...variantProps} />
      ) : (
        <KeyVisualNonClassic {...(variantProps as KeyVisualNonClassicProps)} />
      )}

      {showAwardCollection && (
        <div className="key-visual__awards center--content" id="awards">
          <AwardCollection awards={awards} device={props.device} inKeyVisual />
        </div>
      )}
    </section>
  );
}
